import type { ActiveReportArtifact, ActiveReportDescriptor } from "~/utils/types/assistant-report-artifact";

import {
  ActiveReportArtifactSchema,
  ActiveReportDescriptorSchema,
  ReportArtifactCommitSchema,
} from "~/utils/types/assistant-report-artifact";

import { reportCoverageLine } from "./query-methodology-view-model";
import { reportArtifactVersionKey } from "./report-column-state";

export type CommitRejectionReason =
  "malformed" | "wrong-org" | "missing-current" | "stale-version" | "wrong-artifact" | "duplicate-candidate";

export interface ReportArtifactSummary {
  artifactId: string;
  version: number;
  title: string;
  rowCount: number;
  coverage: string;
  asOf?: string;
}

export type ReportArtifactEntry =
  { kind: "full"; artifact: ActiveReportArtifact; summary: ReportArtifactSummary } | { kind: "receipt"; summary: ReportArtifactSummary };

export interface ReportArtifactState {
  activeArtifactKey: string | null;
  artifacts: Record<string, ReportArtifactEntry>;
}

export interface ApplyReportCommitResult {
  state: ReportArtifactState;
  artifactKey?: string;
  applied: boolean;
  rejectionReason?: CommitRejectionReason;
}

export const emptyReportArtifactState = (): ReportArtifactState => ({ activeArtifactKey: null, artifacts: {} });

export const reportArtifactSummary = (artifact: ActiveReportArtifact): ReportArtifactSummary => ({
  artifactId: artifact.artifactId,
  version: artifact.version,
  title: artifact.presentation.title,
  rowCount: artifact.dataset.rowCount,
  coverage: reportCoverageLine(
    artifact.dataset.methodology.coverage,
    artifact.dataset.presentationManifest?.coverageCopy,
    artifact.dataset.methodology,
  ),
  ...(artifact.dataset.freshness?.asOf ? { asOf: artifact.dataset.freshness.asOf } : {}),
});

export const reportCommitRejectionCopy = (hasArtifact: boolean): string =>
  hasArtifact ? "Couldn't update the report. The previous version is still shown." : "Couldn't update the report.";

export const reportArtifactEntry = (state: ReportArtifactState, artifactKey: string | null): ReportArtifactEntry | null =>
  artifactKey ? (state.artifacts[artifactKey] ?? null) : null;

export const fullReportArtifact = (state: ReportArtifactState, artifactKey: string | null): ActiveReportArtifact | null => {
  const entry = reportArtifactEntry(state, artifactKey);
  return entry?.kind === "full" ? entry.artifact : null;
};

export const activeReportArtifact = (state: ReportArtifactState): ActiveReportArtifact | null =>
  fullReportArtifact(state, state.activeArtifactKey);

/** Keep datasets only for the authoritative artifact and the artifact the user is actively viewing. */
export const retainReportArtifactDatasets = (state: ReportArtifactState, panelArtifactKey: string | null): ReportArtifactState => {
  const retainedKeys = new Set(
    [state.activeArtifactKey, panelArtifactKey].filter((artifactKey): artifactKey is string => artifactKey !== null),
  );
  const artifacts = Object.fromEntries(
    Object.entries(state.artifacts).map(([artifactKey, entry]): [string, ReportArtifactEntry] => [
      artifactKey,
      entry.kind === "full" && !retainedKeys.has(artifactKey) ? { kind: "receipt", summary: entry.summary } : entry,
    ]),
  );
  return { ...state, artifacts };
};

export const applyReportCommit = (
  current: ReportArtifactState,
  rawCommit: unknown,
  orgId: string,
  panelArtifactKey: string | null = null,
): ApplyReportCommitResult => {
  if (!rawCommit) {
    return { state: current, applied: false };
  }
  const parsedCommit = ReportArtifactCommitSchema.safeParse(rawCommit);
  if (!parsedCommit.success) {
    return { state: current, applied: false, rejectionReason: "malformed" };
  }
  const commit = parsedCommit.data;
  if (commit.orgId !== orgId) {
    return { state: current, applied: false, rejectionReason: "wrong-org" };
  }
  if (commit.source === "candidate") {
    if (Object.values(current.artifacts).some(entry => entry.summary.artifactId === commit.artifactId)) {
      return { state: current, applied: false, rejectionReason: "duplicate-candidate" };
    }
    if (!commit.dataset) {
      return { state: current, applied: false, rejectionReason: "malformed" };
    }
    const next = ActiveReportArtifactSchema.safeParse({
      artifactKind: "report-table",
      orgId,
      artifactId: commit.artifactId,
      version: 1,
      dataset: commit.dataset,
      presentation: commit.presentation,
    });
    if (!next.success) {
      return { state: current, applied: false, rejectionReason: "malformed" };
    }
    const artifactKey = reportArtifactVersionKey(next.data);
    const nextState = retainReportArtifactDatasets(
      {
        activeArtifactKey: artifactKey,
        artifacts: {
          ...current.artifacts,
          [artifactKey]: { kind: "full", artifact: next.data, summary: reportArtifactSummary(next.data) },
        },
      },
      panelArtifactKey,
    );
    return { state: nextState, artifactKey, applied: true };
  }
  const active = activeReportArtifact(current);
  if (!active) {
    return { state: current, applied: false, rejectionReason: "missing-current" };
  }
  if (active.artifactId !== commit.artifactId) {
    return { state: current, applied: false, rejectionReason: "wrong-artifact" };
  }
  if (active.version !== commit.expectedVersion) {
    return { state: current, applied: false, rejectionReason: "stale-version" };
  }
  const next = ActiveReportArtifactSchema.safeParse({
    ...active,
    version: active.version + 1,
    presentation: commit.presentation,
  });
  if (!next.success) {
    return { state: current, applied: false, rejectionReason: "malformed" };
  }
  const nextArtifact = { ...next.data, dataset: active.dataset };
  const artifactKey = reportArtifactVersionKey(nextArtifact);
  const nextState = retainReportArtifactDatasets(
    {
      ...current,
      activeArtifactKey: artifactKey,
      artifacts: {
        ...current.artifacts,
        [artifactKey]: { kind: "full", artifact: nextArtifact, summary: reportArtifactSummary(nextArtifact) },
      },
    },
    panelArtifactKey,
  );
  return { state: nextState, artifactKey, applied: true };
};

export const buildActiveReportDescriptor = (state: ReportArtifactState): ActiveReportDescriptor | undefined => {
  const artifact = activeReportArtifact(state);
  if (!artifact) {
    return undefined;
  }
  return ActiveReportDescriptorSchema.parse({
    artifactKind: "report-table",
    orgId: artifact.orgId,
    artifactId: artifact.artifactId,
    version: artifact.version,
    title: artifact.presentation.title,
    availableFields: artifact.dataset.fields,
    publishedFieldKeys: artifact.presentation.fieldKeys,
    unsafeDisplayFieldKeys: artifact.dataset.unsafeDisplayFieldKeys,
    presentationManifest: artifact.dataset.presentationManifest,
    sort: artifact.presentation.sort,
    methodology: artifact.dataset.methodology,
  });
};

/**
 * Post-turn outcome line only. While a turn is working the canvas says nothing —
 * all process narration lives in the chat panel (chat narrates, canvas presents).
 */
export type WorkspaceDisposition =
  "Report updated" | "Report unchanged" | "Report unchanged · the latest analysis failed" | "No report was created for this answer";

export const deriveWorkspaceDisposition = (input: {
  phase: "success" | "failure";
  hasArtifact: boolean;
  commitApplied?: boolean;
}): WorkspaceDisposition | null => {
  if (input.phase === "failure") {
    return input.hasArtifact ? "Report unchanged · the latest analysis failed" : "No report was created for this answer";
  }
  if (input.commitApplied) {
    return "Report updated";
  }
  return input.hasArtifact ? "Report unchanged" : null;
};

/**
 * What the user actually sees. Happy-path dispositions are system status masquerading as
 * content (the artifact card already communicates them), so only failure outcomes render;
 * the full disposition is still derived and kept in state for telemetry and tests.
 */
export const visibleDisposition = (disposition: WorkspaceDisposition | null): WorkspaceDisposition | null =>
  disposition === "Report updated" || disposition === "Report unchanged" ? null : disposition;
